import { useCallback, useMemo } from 'react';

import { Stack } from '@lyearn/ui';
import RadioGroup from '@/components/RadioGroup';
import { AvailableOptionTypeEnum } from '@/types/schema';

import { RadioGroupProps, RadioOption } from '../../../../RadioGroup/types';
import { isItemSelected } from '../../../utils';
import { AvailableOptionsProps } from './types';

const OVERRIDES: RadioGroupProps<string>['overrides'] = {
  radioButtonProps: {
    overrides: {
      container: {
        justifyContent: 'between',
        direction: 'row-reverse',
      },
      descriptionProps: {
        className: 'max-w-384',
        numberOfLines: 2,
      },
    },
  },
};

export const RadioGroupAvailableOption = (props: AvailableOptionsProps) => {
  const { availableOption, selectedItemsOfGroup, onMutate } = props;
  const { default: defaultValue, children } = availableOption;

  const options = useMemo(
    () =>
      children.map((child) => ({
        id: child.key,
        label: child.label,
        description: child.description,
      })),
    [children],
  );

  const selectedId = useMemo(() => {
    const selectedOption = options.find((option) =>
      isItemSelected(selectedItemsOfGroup, { value: option.id, label: option.label }),
    );
    if (!selectedOption) {
      onMutate?.([defaultValue], []);
    }
    return selectedOption ? selectedOption.id : defaultValue;
  }, [defaultValue, onMutate, options, selectedItemsOfGroup]);

  const onChange = useCallback(
    (_: string, changedOption: RadioOption<string>) => {
      onMutate?.([changedOption.id], [selectedId]);
    },
    [onMutate, selectedId],
  );

  return (
    <Stack className="w-full rounded-8 bg-bg-secondary mb-10 mt-2 py-16 px-20">
      <RadioGroup<string>
        className="w-full"
        direction="col"
        gap={20}
        options={options}
        overrides={OVERRIDES}
        selectedId={selectedId}
        type="text"
        onChange={onChange}
      />
    </Stack>
  );
};

const AVAILABLE_OPTION_COMPONENTS: Record<
  AvailableOptionTypeEnum,
  (props: AvailableOptionsProps) => JSX.Element
> = {
  [AvailableOptionTypeEnum.RadioGroup]: RadioGroupAvailableOption,
};

export default AVAILABLE_OPTION_COMPONENTS;
